import React, { useState, useEffect, useContext } from 'react';
import { OrganizationChart } from 'primereact/organizationchart';
import './hierarchy.css';
import { Card } from 'primereact/card';
import { PropTypes } from 'prop-types';
import ScrollContainer from 'react-indiana-drag-scroll';
import _ from 'lodash';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { defineMessages, useIntl } from 'react-intl';
import { ACCOUNT_HIERARCHY, ROOT_ACCOUNT, VIEWER_ROLE } from './hierachyQueries';
import { giveSystemUserExternalSupportRoleInAccount } from './hierarchyMutations';
import { AccountContext } from '../../../components/AccountContext';
import sanitizeDataForOrgChart from './parseHierarchyData';
import { Loading } from '../../../components/Loading';
import NodeSelectedModal from './NodeSelectedModal';
import NodeTemplate from './NodeTemplate';
import apollo from '../../../services/apollo';
import { growlErrors, growlActionSuccess } from '../../../utils/NotificationUtil';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { AccountType } from '../../../shared/Constants';
import ConfirmDeleteAccountDialog from './ConfirmDeleteAccountDialog';
import AccountsInHierarchyDeleteDialog from './AccountsInHierarchyDeleteDialog';
import CopyJobProfilesForAccountDialog from './CopyJobProfilesForAccountDialog';

const intlMessages = defineMessages({
  tableView: {
    id: 'account.hierarchy.tableView.label',
    defaultMessage: 'Table View',
  },
  hierarchicalView: {
    id: 'account.hierarchy.hierarchicalView.label',
    defaultMessage: 'Hierarchical View',
  },
  logo: {
    id: 'account.hierarchy.logo.label',
    defaultMessage: 'Logo',
  },
  filterByAccountId: {
    id: 'account.hierarchy.filterById.placeholder',
    defaultMessage: 'filter by account id ...',
  },
  filterByAccountName: {
    id: 'account.hierarchy.filterByAccountName.placeholder',
    defaultMessage: 'filter by account name ...',
  },
  filterByAccountType: {
    id: 'account.hierarchy.filterByAccountType.placeholder',
    defaultMessage: 'filter by account type ...',
  },
  successNoti: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
});

const AccountsHierarchy = ({ onlyFamilyAccounts, header }) => {
  const [showAccountToDeleteDialog, setShowAccountToDeleteDialog] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [accountsInHierarchyToDelete, setAccountsInHierarchyToDelete] = useState([]);
  const [showCopyJobProfilesForAccountDialog, setShowCopyJobProfilesForAccountDialog] = useState(false);
  const [accountToCopyJobProfilesFor, setAccountToCopyJobProfilesFor] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [treeData, setTreeData] = useState(null);
  const [canSeeDemoCreditsTransferred, setCanSeeDemoCreditsTransferred] = useState(false);
  const [currentAccountType, setCurrentAccountType] = useState(null);
  const [userRoleInAccount, setUserRoleInAccount] = useState(null);

  const currentAccount = useContext(AccountContext);
  const { formatMessage } = useIntl();
  const growl = useContext(NotificationContext);

  const fetchData = () => {
    apollo.query({ query: ROOT_ACCOUNT, variables: { accountId: currentAccount } }).then((result) => {
      const rootAccount = result.data;

      const rootNode = {
        id: rootAccount.account.id,
        parentId: null,
        brandDisplayName: rootAccount.account.displayName,
        brandLogo: rootAccount.account.brandingInformation.logo.url,
        accountType: rootAccount.account.type,
        isSubAccount: false,
        isRoot: true,
        expanded: true,
        depth: 0,
        children: [],
      };

      apollo
        .query({ query: ACCOUNT_HIERARCHY, variables: { accountId: currentAccount }, fetchPolicy: 'network-only' })
        .then((accountHierarchyResult) => {
          const flatData = _.map(accountHierarchyResult.data.account.accountHierarchy, (account) => ({
            id: account.descendantId,
            parentId: account.parentId,
            hasRoleInAccount: account.hasRoleInAccount,
            brandDisplayName: account.descendantBrandDisplayName,
            brandLogo: account.descendantBrandLogo,
            accountType: account.descendantType,
            isSubAccount: account.sameFamily,
            isRoot: false,
            expanded: true,
            depth: account.depth,
            children: [],
          }));

          apollo.query({ query: VIEWER_ROLE }).then((viewerResult) => {
            setTableData(onlyFamilyAccounts ? _.filter(flatData, (acc) => acc.isSubAccount === true) : flatData);
            setTreeData(sanitizeDataForOrgChart(flatData, rootNode, onlyFamilyAccounts));

            const {
              viewer: { roles },
            } = viewerResult.data;
            const accountRole = _.find(roles, (r) => +r.account.id === +currentAccount);
            const accountType = accountRole.account.type;
            const {
              role: { name },
            } = accountRole;

            setUserRoleInAccount(name);
            setCurrentAccountType(accountType);

            setCanSeeDemoCreditsTransferred(
              (accountType === AccountType.CP || accountType === AccountType.MCP) &&
                (name === 'owner' || name === 'admin' || name === 'external-support'),
            );
          });
        });
    });
  };

  useEffect(() => {
    fetchData();

    return () => {
      setTreeData(null);
      setTableData(null);
    };
    // eslint-disable-next-line
  }, [currentAccount, onlyFamilyAccounts]);

  const onOrgChartNodeSelect = (event) => {
    setSelectedNode(event.node);
  };

  const onOrgChartNodeUnselect = () => {
    setSelectedNode(null);
  };

  const tableLogoTemplate = (rawData) => (
    <div style={{ textAlign: 'center' }}>
      {rawData.brandLogo ? (
        <img
          src={rawData.brandLogo}
          alt={rawData.brandLogo}
          style={{
            height: '64px',
            width: 'auto',
            maxWidth: '100%',
            objectFit: 'contain',
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '5px',
          }}
        />
      ) : (
        <span>N/A</span>
      )}
    </div>
  );

  const tableSelectNodeTemplate = (data) => (
    <Button
      icon="pi pi-eye"
      className="p-button-secondary"
      onClick={() => {
        setSelectedNode(data);
      }}
    />
  );

  const isDeleteAccountButtonDisabled = () => {
    return !(userRoleInAccount === 'super-admin' && currentAccountType === AccountType.GPI);
  };

  const isCopyJobProfileButtonDisabled = () => {
    return !(currentAccountType === AccountType.GPI || currentAccountType === AccountType.GSC);
  };

  const deleteTemplate = (data) => (
    <Button
      icon="pi pi-trash"
      className="p-button-secondary"
      disabled={isDeleteAccountButtonDisabled()}
      onClick={() => {
        setAccountToDelete(data);
        setShowAccountToDeleteDialog(true);
      }}
    />
  );

  const copyJobProfileTemplate = (data) => (
    <Button
      icon="pi pi-copy"
      className="p-button-secondary"
      disabled={isCopyJobProfileButtonDisabled()}
      onClick={() => {
        setAccountToCopyJobProfilesFor(data.id);
        setShowCopyJobProfilesForAccountDialog(true);
      }}
    />
  );

  const addExternalSupportRoleButton = (data) => (
    <Button
      icon="pi pi-user-plus"
      className="p-button-secondary"
      disabled={data.hasRoleInAccount}
      onClick={() => {
        giveSystemUserExternalSupportRoleInAccount(data.id)
          .then(() => {
            growlActionSuccess(growl, formatMessage(intlMessages.successNoti));
          })
          .catch((errors) => {
            growlErrors(growl, errors);
          });
      }}
    />
  );

  return (
    <div className="brand-detailed-wrapper">
      <div
        className="p-panel p-component"
        style={{
          paddingTop: '20px',
          backgroundColor: 'whitesmoke',
          overflow: 'auto',
        }}
      >
        <Card title={header} style={{ textAlign: 'center', textTransform: 'uppercase' }} />

        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} style={{ height: '100vh' }}>
          <TabPanel header={formatMessage(intlMessages.tableView)}>
            {tableData ? (
              <DataTable value={tableData} style={{ textAlign: 'center' }} paginator rows={5} responsive>
                <Column
                  field="id"
                  filterPlaceholder={formatMessage(intlMessages.filterByAccountId)}
                  filter
                  filterMatchMode="contains"
                  style={{ width: '12em' }}
                />
                <Column
                  field="brandDisplayName"
                  filterPlaceholder={formatMessage(intlMessages.filterByAccountName)}
                  filter
                  filterMatchMode="contains"
                />
                <Column
                  field="brandLogo"
                  header={formatMessage(intlMessages.logo)}
                  body={tableLogoTemplate}
                  style={{ width: '8em' }}
                />
                <Column
                  field="accountType"
                  filter
                  filterPlaceholder={formatMessage(intlMessages.filterByAccountType)}
                  filterMatchMode="startsWith"
                />
                <Column field="id" body={addExternalSupportRoleButton} style={{ width: '4em' }} />
                <Column field="id" body={tableSelectNodeTemplate} style={{ width: '4em' }} />
                <Column field="id" body={deleteTemplate} style={{ width: '4em' }} />
                <Column field="id" body={copyJobProfileTemplate} style={{ width: '4em' }} />
              </DataTable>
            ) : (
              <Loading />
            )}
          </TabPanel>

          <TabPanel header={formatMessage(intlMessages.hierarchicalView)}>
            {treeData ? (
              <ScrollContainer>
                <OrganizationChart
                  selectionMode="single"
                  selection={selectedNode}
                  onNodeSelect={(e) => onOrgChartNodeSelect(e)}
                  onNodeUnselect={() => onOrgChartNodeUnselect()}
                  nodeTemplate={NodeTemplate}
                  value={treeData}
                />
              </ScrollContainer>
            ) : (
              <Loading />
            )}
          </TabPanel>
        </TabView>
      </div>
      {selectedNode && (
        <NodeSelectedModal
          selectedNode={selectedNode}
          onHide={() => setSelectedNode(null)}
          canSeeDemoCreditsTransferred={canSeeDemoCreditsTransferred}
        />
      )}
      {showAccountToDeleteDialog && (
        <ConfirmDeleteAccountDialog
          accountToDelete={accountToDelete}
          hide={() => setShowAccountToDeleteDialog(false)}
          setAccountToDelete={setAccountToDelete}
          setAccountsInHierarchyToDelete={setAccountsInHierarchyToDelete}
          fetchData={fetchData}
        />
      )}
      {accountsInHierarchyToDelete.length > 0 && (
        <AccountsInHierarchyDeleteDialog
          accountToDelete={accountToDelete}
          accountsInHierarchyToDelete={accountsInHierarchyToDelete}
          hide={() => setAccountsInHierarchyToDelete([])}
          setAccountToDelete={setAccountToDelete}
          setAccountsInHierarchyToDelete={setAccountsInHierarchyToDelete}
          fetchData={fetchData}
        />
      )}
      {showCopyJobProfilesForAccountDialog && (
        <CopyJobProfilesForAccountDialog
          accountToCopyJobProfilesFor={accountToCopyJobProfilesFor}
          hide={() => setShowCopyJobProfilesForAccountDialog(false)}
        />
      )}
    </div>
  );
};

AccountsHierarchy.propTypes = {
  header: PropTypes.string.isRequired,
  onlyFamilyAccounts: PropTypes.bool.isRequired,
};

export default AccountsHierarchy;
